/**
 * Enhanced Patient Documents Component
 * Premium UX, Production-Grade, Therapist/Psychiatrist Focused
 * Comprehensive document management with categories, viewer, search, and organization
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
  InputAdornment,
  useTheme,
  alpha,
  CardMedia,
} from '@mui/material';
import {
  Upload,
  Delete,
  Download,
  Visibility,
  PictureAsPdf,
  Image,
  Description,
  Folder,
  Search,
  FilterList,
  CheckCircle,
  Warning,
  CloudUpload,
  MoreVert,
  Share,
  Edit,
  VerifiedUser,
  Unpublished,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
// Optional: react-dropzone for drag-and-drop upload
// import { useDropzone } from 'react-dropzone';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const DropzoneContainer = styled(Paper)(({ theme, isDragActive }) => ({
  padding: theme.spacing(4),
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: isDragActive
    ? alpha(theme.palette.primary.main, 0.05)
    : alpha(theme.palette.background.paper, 0.8),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    background: alpha(theme.palette.primary.main, 0.02),
  },
}));

const DocumentTypeIcon = ({ type }) => {
  const icons = {
    prescription: <Description color="primary" />,
    lab_result: <PictureAsPdf color="error" />,
    consultation_note: <Description color="info" />,
    imaging: <Image color="warning" />,
    medical_certificate: <VerifiedUser color="success" />,
    vaccination_record: <CheckCircle color="success" />,
    insurance_card: <Description color="default" />,
    identity_proof: <VerifiedUser color="primary" />,
    other: <Description color="default" />,
  };
  return icons[type] || <Description />;
};

const DOCUMENT_TYPES = [
  { value: 'prescription', label: 'Prescription', icon: 'Description' },
  { value: 'lab_result', label: 'Lab Result', icon: 'PictureAsPdf' },
  { value: 'consultation_note', label: 'Consultation Note', icon: 'Description' },
  { value: 'imaging', label: 'Imaging Report', icon: 'Image' },
  { value: 'medical_certificate', label: 'Medical Certificate', icon: 'VerifiedUser' },
  { value: 'vaccination_record', label: 'Vaccination Record', icon: 'CheckCircle' },
  { value: 'insurance_card', label: 'Insurance Card', icon: 'Description' },
  { value: 'identity_proof', label: 'Identity Proof', icon: 'VerifiedUser' },
  { value: 'other', label: 'Other', icon: 'Description' },
];

const EnhancedPatientDocuments = ({ patientId: propPatientId, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id: paramPatientId } = useParams();
  const finalPatientId = propPatientId || paramPatientId;

  // Local state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewerDialogOpen, setViewerDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  // Upload form state
  const [uploadFormData, setUploadFormData] = useState({
    document_type: 'other',
    title: '',
    description: '',
    visibility: 'patient_doctor',
  });

  // Filter documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(doc => doc.document_type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.title?.toLowerCase().includes(searchLower) ||
        doc.description?.toLowerCase().includes(searchLower) ||
        doc.document_type?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [documents, filterType, searchTerm]);

  // Group documents by type
  const documentsByType = useMemo(() => {
    const grouped = {};
    filteredDocuments.forEach(doc => {
      const type = doc.document_type || 'other';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(doc);
    });
    return grouped;
  }, [filteredDocuments]);

  // File input handler (fallback if dropzone not available)
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setUploadFiles(prev => [...prev, ...files]);
  };

  // Drag and drop handlers (simple implementation without react-dropzone)
  const [isDragActive, setIsDragActive] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    // Filter by allowed types
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    const filteredFiles = files.filter(file => 
      allowedTypes.includes(file.type) || 
      file.name.match(/\.(pdf|jpg|jpeg|png|gif)$/i)
    );
    setUploadFiles(prev => [...prev, ...filteredFiles]);
  };

  const getRootProps = () => ({
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onClick: () => document.getElementById('file-input')?.click(),
  });

  const getInputProps = () => ({
    id: 'file-input',
    type: 'file',
    multiple: true,
    accept: '.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx',
    onChange: handleFileSelect,
    style: { display: 'none' },
  });

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;

    setLoading(true);
    // Upload logic here
    // For now, simulate upload
    setTimeout(() => {
      const newDocs = uploadFiles.map((file, index) => ({
        id: Date.now() + index,
        title: uploadFormData.title || file.name,
        document_type: uploadFormData.document_type,
        description: uploadFormData.description,
        file_name: file.name,
        file_size: file.size,
        created_at: new Date().toISOString(),
        is_verified: false,
        visibility: uploadFormData.visibility,
      }));
      setDocuments(prev => [...newDocs, ...prev]);
      setUploadFiles([]);
      setUploadFormData({
        document_type: 'other',
        title: '',
        description: '',
        visibility: 'patient_doctor',
      });
      setUploadDialogOpen(false);
      setLoading(false);
    }, 2000);
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setViewerDialogOpen(true);
  };

  const handleDeleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getDocumentCount = (type) => {
    return documents.filter(doc => doc.document_type === type).length;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Patient Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and organize patient medical documents
          </Typography>
        </Box>
        {role === 'doctor' && (
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Documents
          </Button>
        )}
      </Box>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Documents
                  </Typography>
                  <Folder color="primary" />
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {documents.length}
                </Typography>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>

        {DOCUMENT_TYPES.slice(0, 3).map((type, index) => (
          <Grid item xs={12} sm={6} md={3} key={type.value}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 + index * 0.1 }}
            >
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {type.label}
                    </Typography>
                    <DocumentTypeIcon type={type.value} />
                  </Box>
                  <Typography variant="h4" fontWeight={600}>
                    {getDocumentCount(type.value)}
                  </Typography>
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Document Type</InputLabel>
              <Select
                value={filterType}
                label="Document Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                {DOCUMENT_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Documents List/Grid */}
      <StyledCard>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Documents
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {filteredDocuments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {documents.length === 0
                  ? 'No documents uploaded yet'
                  : 'No documents match your filters'}
              </Typography>
              {role === 'doctor' && documents.length === 0 && (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => setUploadDialogOpen(true)}
                >
                  Upload First Document
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredDocuments.map((doc, index) => (
                <Grid item xs={12} sm={6} md={4} key={doc.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          transform: 'translateY(-2px)',
                        },
                      }}
                      onClick={() => handleViewDocument(doc)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}>
                          <DocumentTypeIcon type={doc.document_type} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {doc.title || doc.file_name || 'Untitled Document'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {doc.document_type && DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label}
                          </Typography>
                          {doc.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {doc.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip
                              label={format(parseISO(doc.created_at), 'MMM dd, yyyy')}
                              size="small"
                              variant="outlined"
                            />
                            {doc.file_size && (
                              <Chip
                                label={formatFileSize(doc.file_size)}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {doc.is_verified ? (
                              <Chip
                                icon={<VerifiedUser />}
                                label="Verified"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip
                                icon={<Unpublished />}
                                label="Unverified"
                                size="small"
                                color="default"
                              />
                            )}
                          </Stack>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDocument(doc);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                          {role === 'doctor' && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDocument(doc.id);
                              }}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </StyledCard>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or click to select files
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported: PDF, Images, Word Documents (Max 10MB per file)
              </Typography>
            </DropzoneContainer>

            {uploadFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({uploadFiles.length})
                </Typography>
                <List>
                  {uploadFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={file.name}
                        secondary={formatFileSize(file.size)}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== index))}
                      >
                        <Delete />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <TextField
              fullWidth
              label="Title"
              value={uploadFormData.title}
              onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
              placeholder="Enter document title"
            />

            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={uploadFormData.document_type}
                label="Document Type"
                onChange={(e) => setUploadFormData({ ...uploadFormData, document_type: e.target.value })}
              >
                {DOCUMENT_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={uploadFormData.description}
              onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
              placeholder="Enter document description (optional)"
            />

            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={uploadFormData.visibility}
                label="Visibility"
                onChange={(e) => setUploadFormData({ ...uploadFormData, visibility: e.target.value })}
              >
                <MenuItem value="patient_only">Patient Only</MenuItem>
                <MenuItem value="patient_doctor">Patient & Doctor</MenuItem>
                <MenuItem value="patient_doctor_receptionist">Patient, Doctor & Receptionist</MenuItem>
                <MenuItem value="all_staff">All Staff</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setUploadFiles([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploadFiles.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Upload />}
          >
            {loading ? 'Uploading...' : `Upload ${uploadFiles.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={viewerDialogOpen}
        onClose={() => setViewerDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedDocument?.title || selectedDocument?.file_name || 'Document Viewer'}
            </Typography>
            <Box>
              <IconButton>
                <Download />
              </IconButton>
              {role === 'doctor' && (
                <IconButton>
                  <Share />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Document preview would appear here. In production, integrate PDF.js or image viewer.
              </Typography>
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Document Details</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Type:</Typography>
                    <Typography variant="body2">
                      {DOCUMENT_TYPES.find(t => t.value === selectedDocument.document_type)?.label || selectedDocument.document_type}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Uploaded:</Typography>
                    <Typography variant="body2">
                      {selectedDocument.created_at && format(parseISO(selectedDocument.created_at), 'MMM dd, yyyy hh:mm a')}
                    </Typography>
                  </Grid>
                  {selectedDocument.description && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Description:</Typography>
                      <Typography variant="body2">{selectedDocument.description}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewerDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedPatientDocuments;

